import { FC, PropsWithChildren, ReactNode } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { trackingIds } from '../../../../../tracking-ids';

export interface FilterTraySectionProps {
  label: ReactNode;
  icon?: ReactNode;
  onClear?: () => void;
  showClear?: boolean;
  disableClear?: boolean;
  clearTrackingId?: string;
}

export const FilterTraySection: FC<PropsWithChildren<FilterTraySectionProps>> = ({
  label,
  icon,
  onClear,
  showClear = true,
  children,
  disableClear = false,
  clearTrackingId = trackingIds.callIntel.filterTraySectionClearAction,
}) => {
  const { t } = useTranslation('analytics');

  return (
    <div css={styles.section}>
      <div css={styles.header}>
        <div css={styles.labelWrapper}>
          {icon && <span>{icon}</span>}
          {typeof label === 'string' ? (
            <Text weight='bold' size='large'>
              {label}
            </Text>
          ) : (
            label
          )}
        </div>
        {showClear && (
          <Button variant='tertiary' onClick={onClear} disabled={disableClear} trackingId={clearTrackingId}>
            {t('Clear')}
          </Button>
        )}
      </div>
      {children}
    </div>
  );
};

const styles = {
  section: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2, 4)};
  `,

  header: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(2)};
  `,

  label: css`
    font-weight: ${theme.font.weight.semibold};
    color: ${theme.colors.neutral90};
  `,
  labelWrapper: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing(1)};
  `,
};
